// stores 
import * as ProtocolsStore from '../../../redux/middleware/MedicalProtocolMiddleware';
import * as ProtocolConsumptionSyncProcessStore from '../../../redux/middleware/ProtocolConsumptionSyncProcessMiddleware';
import * as ProtocolConsumptionStore from '../../../redux/middleware/ProtocolConsumptionMiddleware';
import * as ContractStore from '../../../redux/middleware/ContractMiddleware';


// state
import { AppState } from '../../../redux/reducers';
import { Protocol } from '../../../models/Entities/Protocols/Protocol';
import { ProtocolConsumptionSyncProcess } from '../../../models/Entities/Protocols/ProtocolConsumptionSyncProcess';
import { ErrorType } from '../../../models/HttpError';
import { IConsumptionsFilters } from '../../../models/Filters/ConsumptionsFilters';

//#region Estado del proceso

/**
 * A partir de acá un proceso que sigue reportándose "en curso" se considera
 * trabado y se habilita el reintento. El backend no avisa cuando se cuelga:
 * sin este límite el botón queda deshabilitado para siempre y no queda forma
 * de volver a lanzar la sincronización.
 */
export const STALL_THRESHOLD_MINUTES = 2;

const MINUTE_MS = 60_000;

/** Estados que el backend reporta mientras el proceso sigue trabajando. */
export const isRunningSyncStatus = (status: number | undefined): boolean =>
    status === 1 || status === 2;

/**
 * La corrida terminó bien. Es el único desenlace en el que pide el análisis de
 * IA: si falló, la liquidación quedó incompleta y no hay nada que analizar.
 */
export const isFinishedSyncStatus = (status: number | undefined): boolean => status === 3;

/**
 * La corrida ya no está corriendo, terminara como terminara. Es lo que habilita
 * pedir el análisis de IA: el analista puede querer mirar también una corrida
 * que terminó con avisos o falló, y el servidor decide si tiene qué analizar.
 */
export const isTerminalSyncStatus = (status: number | undefined): boolean =>
    status !== undefined && !isRunningSyncStatus(status);

export interface SyncProgressInput {
    status: number | undefined;
    /** Hay un alta de proceso en vuelo (el POST todavía no respondió). */
    isAdding: boolean;
    /** El alta del proceso falló. */
    failureAdding: boolean;
    /** Desde cuándo se lo viene midiendo; undefined si no se pudo determinar. */
    startedAtMs: number | undefined;
    nowMs: number;
    thresholdMinutes?: number;
}

export interface SyncProgress {
    isRunning: boolean;
    /** Minutos que lleva corriendo; undefined si no hay desde cuándo medir. */
    elapsedMinutes: number | undefined;
    /** Sigue "en curso" pero pasó el límite. */
    isStalled: boolean;
    /** Falló o se trabó: en ambos casos lo que corresponde es volver a lanzarlo. */
    canRetry: boolean;
}

/**
 * Decide si el proceso avanza, se trabó o falló. Es una función pura sobre el
 * reloj que le pasan para que el paso del tiempo sea parte de la prueba y no
 * algo que haya que esperar.
 */
export const evaluateSyncProgress = (input: SyncProgressInput): SyncProgress => {
    const threshold = input.thresholdMinutes ?? STALL_THRESHOLD_MINUTES;
    const isRunning = isRunningSyncStatus(input.status);

    const elapsedMinutes = isRunning && input.startedAtMs !== undefined && Number.isFinite(input.startedAtMs)
        // Un reloj adelantado en el servidor daría negativo: eso es "recién
        // arrancado", nunca un proceso trabado.
        ? Math.max(0, Math.floor((input.nowMs - input.startedAtMs) / MINUTE_MS))
        : undefined;

    const isStalled = isRunning && elapsedMinutes !== undefined && elapsedMinutes >= threshold;

    // Mientras el alta está en vuelo no se ofrece reintentar: ya hay uno saliendo.
    const canRetry = !input.isAdding && (input.status === 5 || input.failureAdding || isStalled);

    return { isRunning, elapsedMinutes, isStalled, canRetry };
};

//#endregion Estado del proceso

//#region Props

interface IProps {
    ProtocolId: number;
    filters: IConsumptionsFilters
}

interface ConnectedProps {
    isLoading: boolean;
    successLoading: boolean;
    failureLoading: boolean;
    ErrorGetting: ErrorType | null;

    isAdding: boolean;
    successAdding: boolean;
    failureAdding: boolean;
    ErrorAdding: ErrorType | null;

    CurrentProcess: ProtocolConsumptionSyncProcess;
    protocol: Protocol | null;
    haveContractAssociated: boolean;

}

interface MedicalServicesDispatchProps {
    GetMedicalProtocolById: typeof ProtocolsStore.actionCreators.GetMedicalProtocolById;
    GetLastProtocolConsumptionSyncProcessByProtocolId: typeof ProtocolConsumptionSyncProcessStore.ActionCreators.GetLastProtocolConsumptionSyncProcessByProtocolId;
    CreateProtocolConsumptionSyncProcess: typeof ProtocolConsumptionSyncProcessStore.ActionCreators.CreateProtocolConsumptionSyncProcess;
    UpdateProtocolConsumptionSyncProcess: typeof ProtocolConsumptionSyncProcessStore.ActionCreators.UpdateProtocolConsumptionSyncProcess;
    GetAllProtocolConsumptionsByProtocol: typeof ProtocolConsumptionStore.ActionCreators.GetAllProtocolConsumptionsByProtocol;
    GetAllProtocolContractAssociations: typeof ProtocolsStore.actionCreators.GetAllProtocolContractAssociations;
}

export type Props = IProps & ConnectedProps & MedicalServicesDispatchProps;

export const mapStateToProps = (state: AppState, ownProps: IProps) => {

    const AuxProtocol: Protocol | null =  state.MedicalProtocols?.list?.find((protocol: Protocol) => protocol.id === ownProps.ProtocolId) || null;

    return {
        isLoading: state.MedicalProtocols?.isLoadingLastSyncProcess || false,
        successLoading: state.MedicalProtocols?.LoadingLastSyncProcessSuccess || undefined,
        failureLoading: state.MedicalProtocols?.FailGettingLastSyncProcess || undefined,
        ErrorGetting: state.MedicalProtocols?.ErrorGettingLastSyncProcess || null,

        isAdding: state.MedicalProtocols?.isCreatingSyncProcess || false,
        successAdding: state.MedicalProtocols?.CreatingSyncProcessSuccess || undefined,
        failureAdding: state.MedicalProtocols?.FailCreatingSyncProcess || undefined,
        ErrorAdding: state.MedicalProtocols?.ErrorCreatingSyncProcess || null,

        CurrentProcess: AuxProtocol?.lastConsumptionSyncProcess || undefined,
        protocol: AuxProtocol,
        haveContractAssociated: AuxProtocol?.contractAssociation ? true : false

    }
};

export const mapDispatchToProps = {
    ...ProtocolsStore.actionCreators,
    ...ProtocolConsumptionSyncProcessStore.ActionCreators,
    ...ProtocolConsumptionStore.ActionCreators,
    ...ContractStore.actionCreators
};